import React from 'react';
import { Card, Typography } from 'antd';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, Area, AreaChart } from 'recharts';
import { DollarOutlined } from '@ant-design/icons';

const { Title } = Typography;

interface RevenueChartProps {
    data: {
        month: string;
        revenue: number;
        transactions: number;
    }[];
    loading?: boolean;
}

const RevenueChart: React.FC<RevenueChartProps> = ({ data, loading = false }) => {
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 border rounded shadow-sm">
                    <p className="fw-bold mb-2">{`${label}`}</p>
                    <p className="text-primary mb-1">
                        <span className="fw-bold">Gelir: </span>
                        {formatCurrency(payload[0].value)}
                    </p>
                    <p className="text-success mb-0">
                        <span className="fw-bold">İşlem: </span>
                        {payload[1]?.value || 0} adet
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <Card 
            className="border-0 shadow-sm"
            loading={loading}
            title={
                <div className="d-flex align-items-center">
                    <div className="me-2 p-2 bg-primary bg-opacity-10 rounded">
                        <DollarOutlined className="text-primary" />
                    </div>
                    <div>
                        <Title level={5} className="mb-0">Aylık Gelir Trendi</Title>
                        <small className="text-muted">Son 12 ayın gelir ve işlem analizi</small>
                    </div>
                </div>
            }
            bodyStyle={{ padding: '20px' }}
        >
            <div style={{ height: '350px' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <defs>
                            <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#667eea" stopOpacity={0.8}/>
                                <stop offset="95%" stopColor="#667eea" stopOpacity={0.1}/>
                            </linearGradient>
                            <linearGradient id="transactionGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#f093fb" stopOpacity={0.8}/>
                                <stop offset="95%" stopColor="#f093fb" stopOpacity={0.1}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                            dataKey="month" 
                            stroke="#666"
                            fontSize={12}
                            tickLine={false}
                        />
                        <YAxis 
                            yAxisId="revenue"
                            orientation="left"
                            stroke="#667eea"
                            fontSize={12}
                            tickLine={false}
                            tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <YAxis 
                            yAxisId="transactions"
                            orientation="right"
                            stroke="#f093fb"
                            fontSize={12}
                            tickLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                            yAxisId="revenue"
                            type="monotone"
                            dataKey="revenue"
                            stroke="#667eea"
                            strokeWidth={3}
                            fill="url(#revenueGradient)"
                            dot={{ fill: '#667eea', strokeWidth: 2, r: 4 }}
                            activeDot={{ r: 6, stroke: '#667eea', strokeWidth: 2 }}
                        />
                        <Line
                            yAxisId="transactions"
                            type="monotone"
                            dataKey="transactions"
                            stroke="#f093fb"
                            strokeWidth={2}
                            dot={{ fill: '#f093fb', strokeWidth: 2, r: 3 }}
                            activeDot={{ r: 5, stroke: '#f093fb', strokeWidth: 2 }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>

            {/* Alt bilgi */}
            <div className="mt-3 pt-3 border-top">
                <div className="row text-center">
                    <div className="col-4">
                        <div className="d-flex align-items-center justify-content-center">
                            <div className="me-2" style={{ 
                                width: '12px', 
                                height: '12px', 
                                backgroundColor: '#667eea',
                                borderRadius: '50%'
                            }}></div>
                            <small className="text-muted">Gelir ($)</small>
                        </div>
                    </div>
                    <div className="col-4">
                        <div className="d-flex align-items-center justify-content-center">
                            <div className="me-2" style={{ 
                                width: '12px', 
                                height: '12px', 
                                backgroundColor: '#f093fb',
                                borderRadius: '50%'
                            }}></div>
                            <small className="text-muted">İşlem Sayısı</small>
                        </div>
                    </div>
                    <div className="col-4">
                        <small className="text-muted">
                            Toplam: {formatCurrency(data.reduce((sum, item) => sum + item.revenue, 0))}
                        </small>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default RevenueChart; 